import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuthStore, AdminRole } from "../stores/authStore";
import ImpersonationBanner from "./ImpersonationBanner";
import { useThemeStore, Theme } from "../hooks/useTheme";

const ROLE_BADGE: Record<AdminRole, string> = {
  root: "badge-root", admin: "badge-admin", support_l2: "badge-l2", support_l1: "badge-l1",
};
const ROLE_ICON: Record<AdminRole, string> = {
  root: "🔑", admin: "👑", support_l2: "🎧", support_l1: "👤",
};
const ROLES_BELOW: Record<AdminRole, AdminRole[]> = {
  root:       ["admin", "support_l2", "support_l1"],
  admin:      ["support_l2", "support_l1"],
  support_l2: ["support_l1"],
  support_l1: [],
};
const ROLE_LABELS: Record<AdminRole, string> = {
  root: "Root", admin: "Admin", support_l2: "Support L2", support_l1: "Support L1",
};

export default function Shell() {
  const navigate = useNavigate();
  const { role, email, clearAuth, assumedRole, setAssumedRole, hasPermission } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const effectiveRole = assumedRole ?? role;

  function signOut() { clearAuth(); navigate("/login", { replace: true }); }

  const navItem = (to: string, icon: string, label: string, requires?: string) => {
    const allowed = !requires || hasPermission(requires);
    return (
      <NavLink to={to}
        style={({ isActive }) => ({
          display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
          borderRadius: 8, fontSize: 13, fontWeight: isActive ? 700 : 400,
          textDecoration: "none",
          color: !allowed ? "var(--text-muted)" : isActive ? "var(--accent)" : "var(--text)",
          background: isActive ? "var(--accent-dim)" : "transparent",
          opacity: !allowed ? 0.4 : 1,
          pointerEvents: !allowed ? "none" : "auto",
        })}
      >
        {icon} {label}
      </NavLink>
    );
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <div style={{ width: 180, background: "var(--surface)", borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", flexShrink: 0 }}>
        <div style={{ padding: "16px 14px 12px", fontWeight: 900, fontSize: 15, color: "var(--accent)", borderBottom: "1px solid var(--border)" }}>
          ⚡ SQ Admin
        </div>
        <nav style={{ flex: 1, padding: "10px 8px", display: "flex", flexDirection: "column", gap: 2 }}>
          {navItem("/", "📊", "Dashboard")}
          {navItem("/families", "👨‍👩‍👧", "Families", "families:read")}
          {navItem("/backup", "📦", "Backup", "backup:export")}
          {navItem("/metrics", "📈", "Metrics", "metrics:read")}
          <div style={{ height: 1, background: "var(--border)", margin: "6px 0" }} />
          {navItem("/audit", "📋", "Audit Log", "metrics:read")}
          {navItem("/team", "🎧", "Team", "team:invite")}
          {role === "root" && navItem("/permissions", "⚙️", "Permissions")}
          {navItem("/settings", "⚙", "Settings")}
        </nav>
        <div style={{ padding: "10px 12px", borderTop: "1px solid var(--border)", fontSize: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <span className={`badge ${ROLE_BADGE[effectiveRole!]}`}>
              {ROLE_ICON[effectiveRole!]} {ROLE_LABELS[effectiveRole!]}
            </span>
          </div>
          <div style={{ color: "var(--text-muted)", marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email}</div>

          {!assumedRole && role && ROLES_BELOW[role].length > 0 && (
            <select
              value=""
              onChange={(e) => e.target.value && setAssumedRole(e.target.value as AdminRole)}
              style={{ width: "100%", padding: "4px 6px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--surface2)", color: "var(--text-muted)", fontSize: 11, marginBottom: 6 }}
            >
              <option value="">👁 View as…</option>
              {ROLES_BELOW[role].map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          )}

          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value as Theme)}
            style={{ width: "100%", padding: "4px 6px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--surface2)", color: "var(--text-muted)", fontSize: 11, marginBottom: 6 }}
          >
            <option value="dark">🌙 Dark</option>
            <option value="light">☀️ Light</option>
            <option value="midnight">🌌 Midnight</option>
            <option value="high-contrast">⚡ High Contrast</option>
          </select>

          <button className="btn" style={{ width: "100%", fontSize: 11 }} onClick={signOut}>Sign Out</button>
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <ImpersonationBanner />
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
